import { buildMockPromise } from "../api/helpers";
import {
  ItemWithMetaData,
  Item,
  ItemsForReviewResponse,
  ItemTypes,
  Rating,
  Score,
  Type,
  Source,
} from "../api/Items";
import { v4 as uuidv4 } from "uuid";

export async function fetchProfileList(): Promise<ItemsForReviewResponse> {
  return await buildMockPromise<ItemsForReviewResponse>(profileList);
}

export async function fetchPostRateList(): Promise<ItemsForReviewResponse> {
  return await buildMockPromise<ItemsForReviewResponse>(postRateList);
}

export const profileList: ItemsForReviewResponse = buildProfileList();

export const postRateList: ItemsForReviewResponse = buildPostRateList();

function buildProfileList(
  numSuggestedProfiles: number = 4,
  numSystemAdds: number = 8,
  numManualAdds: number = 8
): ItemsForReviewResponse {
  const profiles: ItemWithMetaData[] = [];
  for (let i = 1; i <= numSuggestedProfiles; i++) {
    const profileContent = buildProfileContent();
    profiles.push(
      wrapItemInMetaData(
        buildGenericItem(profileContent, false, false, false),
        ItemTypes?.SOCIAL_MEDIA_PROFILE
      )
    );
  }
  for (let i = 1; i <= numSystemAdds; i++) {
    const profileContent = buildProfileContent();
    profiles.push(
      wrapItemInMetaData(
        buildGenericItem(profileContent, true, false, true),
        ItemTypes?.SOCIAL_MEDIA_PROFILE
      )
    );
  }
  for (let i = 1; i <= numManualAdds; i++) {
    const profileContent = buildProfileContent();
    profiles.push(
      wrapItemInMetaData(
        buildGenericItem(profileContent, false, false, true),
        ItemTypes?.SOCIAL_MEDIA_PROFILE
      )
    );
  }
  return wrapInResponseEnvelope(profiles);
}

function buildPostRateList(numPosts: number = 20): ItemsForReviewResponse {
  const postItems: ItemWithMetaData[] = [];
  for (let i = 0; i < numPosts; i++) {
    const postContent = buildPostContent(i);
    let isManualAdd = false;
    if (i > numPosts / 2) {
      isManualAdd = true;
    }
    postItems.push(
      wrapItemInMetaData(
        buildGenericItem(postContent, isManualAdd),
        ItemTypes?.SOCIAL_MEDIA_POST
      )
    );
  }

  return wrapInResponseEnvelope(postItems);
}

function wrapInResponseEnvelope(
  items: ItemWithMetaData[]
): ItemsForReviewResponse {
  return {
    items: items,
    total: items.length,
    page: 1,
    size: 100,
  };
}

export function wrapItemInMetaData(
  item: Item,
  type: ItemTypes
): ItemWithMetaData {
  return {
    item: item,
    score: buildScores(),
    rating: buildRatings(),
    entities: buildRelevantEntities(),
    type: buildType(type),
    source: buildSource(),
  };
}

function buildScores(): Score[] {
  return [
    {
      external_id: "7afff1bb-07b5-4636-ba27-e45f6cb139a8",
      score: 1,
      name: "constellation_field_names",
      score_type: "recommendation",
      action: "flag",
    },
    {
      external_id: "3839609a-b17e-49bf-8ca7-d4bad7aa983d",
      score: 0,
      name: "constellation_field_unique_ids",
      score_type: "recommendation",
      action: "flag",
    },
    {
      external_id: "2229230f-a51f-4536-9668-bcd73ed177be",
      score: 0.428441,
      name: "constellation_general",
      score_type: "recommendation",
      action: "flag",
    },
    {
      external_id: "2736f94d-6668-4ba1-b0b6-6bb7290bcdba",
      score: 0,
      name: "constellation_field_faces",
      score_type: "recommendation",
      action: "flag",
    },
    {
      external_id: "166f8d1b-fae2-4241-8c0e-a77023e84465",
      score: 0.841302,
      name: "constellation_field_addresses",
      score_type: "recommendation",
      action: "flag",
    },
    {
      external_id: "c4d38b50-649c-41d8-b9cd-df44548d0774",
      score: 0,
      name: "constellation_field_phones",
      score_type: "recommendation",
      action: "flag",
    },
    {
      external_id: "77862596-302f-4862-99a8-ff5f913c120c",
      score: 0,
      name: "constellation_field_occupations",
      score_type: "recommendation",
      action: "flag",
    },
    {
      external_id: "0bdc9281-bd64-44bb-bcb8-f07e2823c50b",
      score: 1,
      name: "constellation_field_emails",
      score_type: "recommendation",
      action: "flag",
    },
    {
      external_id: "a328a01f-2c2d-489c-a4ad-360ec2b8aa79",
      score: 0,
      name: "constellation_field_periods",
      score_type: "recommendation",
      action: "flag",
    },
    {
      external_id: "15801409-1a79-4f54-a9d0-93caf60397b8",
      score: 1.01284,
      name: "constellation_field_dates",
      score_type: "recommendation",
      action: "flag",
    },
    {
      external_id: "d3b47f11-502b-4725-adf3-370cdd6cb08b",
      score: 1,
      name: "constellation_field_locations",
      score_type: "recommendation",
      action: "flag",
    },
    {
      external_id: "9e8db7e3-3f52-44e5-9a1d-1cdd38e0d63b",
      score: 0,
      name: "constellation_field_institutions",
      score_type: "recommendation",
      action: "flag",
    },
  ];
}

function buildRatings(): Rating[] {
  const ratings = [];
  if (Math.floor(Math.random() * 2) === 0) {
    return [];
  }

  if (Math.floor(Math.random() * 2) === 0) {
    ratings.push({
      external_id: uuidv4(),
      rater_uuid: "3fa85f64-5717-4562-b3fc-2c963f66afa6",
      name: ["Drugs", "Harassment", "Sex", "Violence"][
        Math.floor(Math.random() * 4)
      ],
      score_type: "behavior",
    });
  }

  ratings.push(
    ...[
      {
        external_id: uuidv4(),
        rater_uuid: "3fa85f64-5717-4562-b3fc-2c963f66afa6",
        name: "Harassment",
        score_type: "behavior",
      },
      {
        external_id: uuidv4(),
        rater_uuid: "3fa85f64-5717-4562-b3fc-2c963f66afa6",
        name: "Sex",
        score_type: "behavior",
      },
    ]
  );
  return ratings;
}

function buildType(type: ItemTypes): Type {
  return {
    external_id: "85cb817f-2c03-4498-bc9c-a1c70a1a0677",
    name: type,
  };
}

function buildSource(): Source {
  return {
    external_id: "85cb817f-2c03-4498-bc9c-a1c70a1a0677",
    name: "Twitter",
  };
}

export function buildGenericItem(
  content: any,
  isManualAdd: boolean = true,
  isReviewed: boolean = false,
  inReport: boolean = true
): Item {
  return {
    external_id: uuidv4(),
    in_report: inReport,
    manually_added: isManualAdd,
    content: content,
    is_reviewed: isReviewed,
  };
}

function buildProfileContent(): any {
  return {
    url: `https://twitter.com/user/${uuidv4()}`,
  };
}

export function buildPostContent(iterator: number): any {
  const randomSite = ["facebook", "twitter", "instagram", "random"][
    Math.floor(Math.random() * 4)
  ];
  const randomPostType = ["original", "shared", "liked"][
    Math.floor(Math.random() * 3)
  ];
  return {
    social_site: "Twitter",
    profile_url: "https://twitter.com/Snapple",
    created_at: "2022-08-21",
    url: `https://${randomSite}.com/Snapple/status/1517186718730014720`,
    type: randomPostType,
    media: [
      {
        external_url:
          "https://pbs.twimg.com/media/FQ4hRykXEAAqFHm?format=jpg&name=medium",
        type: "image",
        caption: "",
        thumbnail_url: "",
      },
      {
        external_url:
          "https://pbs.twimg.com/media/FQzhISGXEAEX0fB?format=jpg&name=small",
        type: "image",
        caption: "",
        thumbnail_url: "",
      },
    ],
    text: `[${iterator}],New Snapple Zero Sugar doesn't have sugar, so we're giving you the #SnappleFacts #UNSUGARCOATED Stay close to this thread for unsugarcoated facts every day for the rest of April.`,
    site_id: "1517186718730014720",
  };
}

export function buildRelevantEntities(): any {
  return [
    {
      id: 155,
      external_id: "d8af5ea4-981e-49fc-bb4f-86936172db50",
      created_at: "2022-07-26T15:58:48+00:00",
      modified_at: "2022-07-26T15:58:48+00:00",
      is_deleted: false,
      modified_by: "1cdbc89c-73fb-4c58-ab1b-32b2e84d6bae",
      item_id: 28,
      field: "emails",
      value_type: "json",
      value: '{"score": 1.0, "entities": [["example@exa.com", []]]}',
    },
    {
      id: 156,
      external_id: "ea91c9b6-a990-4c11-b707-34b3fb0c9f92",
      created_at: "2022-07-26T15:58:48+00:00",
      modified_at: "2022-07-26T15:58:48+00:00",
      is_deleted: false,
      modified_by: "1cdbc89c-73fb-4c58-ab1b-32b2e84d6bae",
      item_id: 28,
      field: "names",
      value_type: "json",
      value: '{"score": 1.0, "entities": [["Example", []]]}',
    },
    {
      id: 157,
      external_id: "62b71178-bb88-47da-9466-43cde6873776",
      created_at: "2022-07-26T15:58:54+00:00",
      modified_at: "2022-07-26T15:58:54+00:00",
      is_deleted: false,
      modified_by: "1cdbc89c-73fb-4c58-ab1b-32b2e84d6bae",
      item_id: 28,
      field: "dates",
      value_type: "json",
      value:
        '{"score": 1.0088788238716906, "entities": [["1965-10-09", []], ["2022-02-01", []], ["2011-01-01", []], ["2001-01-01", []], ["2015-02-03", []], ["2022-12-12", []], ["2012-01-01", []], ["2002-01-01", []]]}',
    },
    {
      id: 158,
      external_id: "d037d457-1023-49a2-bfdf-271f0dcca092",
      created_at: "2022-07-26T15:58:54+00:00",
      modified_at: "2022-07-26T15:58:54+00:00",
      is_deleted: false,
      modified_by: "1cdbc89c-73fb-4c58-ab1b-32b2e84d6bae",
      item_id: 28,
      field: "addresses",
      value_type: "json",
      value:
        '{"score": 0.8413022539809416, "entities": [["Ave. Broadway L.A. Us California", []]]}',
    },
    {
      id: 159,
      external_id: "91f9c95a-6db4-4217-8833-27f5c9545f1b",
      created_at: "2022-07-26T15:58:55+00:00",
      modified_at: "2022-07-26T15:58:55+00:00",
      is_deleted: false,
      modified_by: "1cdbc89c-73fb-4c58-ab1b-32b2e84d6bae",
      item_id: 28,
      field: "locations",
      value_type: "json",
      value: '{"score": 1.0, "entities": [["36.452, -119.944", []]]}',
    },
    {
      id: 160,
      external_id: "9f64e9a2-b2c6-48f5-bbd2-88a9290643e2",
      created_at: "2022-07-26T15:58:55+00:00",
      modified_at: "2022-07-26T15:58:55+00:00",
      is_deleted: false,
      modified_by: "1cdbc89c-73fb-4c58-ab1b-32b2e84d6bae",
      item_id: 28,
      field: "occupations",
      value_type: "json",
      value: '{"score": 0.0, "entities": []}',
    },
  ];
}
